export const CONTENT_KINDS = [
  "blog",
  "changelog",
  "documentation",
  "report",
] as const;

export type ContentKind = (typeof CONTENT_KINDS)[number];

export const CONTENT_LOCALES = ["en", "ar"] as const;

export type ContentLocale = (typeof CONTENT_LOCALES)[number];

export const CONTENT_VARIANT_STATUSES = [
  "draft",
  "review",
  "scheduled",
  "published",
  "archived",
] as const;

export type ContentVariantStatus = (typeof CONTENT_VARIANT_STATUSES)[number];

export const CONTENT_AUTOMATION_MODES = [
  "manual",
  "assisted",
  "autopilot",
  "paused",
] as const;

export type ContentAutomationMode = (typeof CONTENT_AUTOMATION_MODES)[number];

export const CONTENT_RISK_LEVELS = ["low", "medium", "high"] as const;

export type ContentRiskLevel = (typeof CONTENT_RISK_LEVELS)[number];

export const CONTENT_CLAIM_STATUSES = [
  "pending",
  "verified",
  "interpretation",
  "unsupported",
] as const;

export type ContentClaimStatus = (typeof CONTENT_CLAIM_STATUSES)[number];

export const CONTENT_GENERATION_STATUSES = [
  "queued",
  "processing",
  "completed",
  "failed",
  "cancelled",
] as const;

export type ContentGenerationStatus =
  (typeof CONTENT_GENERATION_STATUSES)[number];

export const CONTENT_PUBLICATION_STATUSES = [
  "queued",
  "processing",
  "published",
  "failed",
  "cancelled",
] as const;

export type ContentPublicationStatus =
  (typeof CONTENT_PUBLICATION_STATUSES)[number];

export type ContentPublicAuthor = {
  name: string;
  /** One short line, e.g. "Founder and CEO". Null renders the name alone. */
  role: string | null;
  avatarUrl: string | null;
  /**
   * Where this person exists off the site: a profile, not a Tabbio page. The
   * credibility of a byline comes from that footprint, not from the byline.
   */
  url: string | null;
};

export type ContentPublicReviewer = { name: string };

export type PublicContentVariantLink = {
  locale: ContentLocale;
  slug: string;
};

export interface PublicContentItem {
  id: string;
  documentId: string;
  kind: ContentKind;
  locale: ContentLocale;
  slug: string;
  title: string;
  excerpt: string;
  seoTitle: string | null;
  seoDescription: string | null;
  imageUrl: string | null;
  /** Accessible name for `imageUrl`. Null or empty means the image is decorative. */
  imageAlt: string | null;
  /** Optional visible caption. Unlike alt text, this is indexed body copy. */
  imageCaption: string | null;
  ogImageUrl: string | null;
  /** Overrides the social-card alt text, which otherwise falls back to the title. */
  ogImageAlt: string | null;
  category: string | null;
  tags: string[];
  author: ContentPublicAuthor;
  reviewer: ContentPublicReviewer | null;
  noIndex: boolean;
  publishedAt: string;
  updatedAt: string;
  /** Word count of the published body, Markdown punctuation stripped. */
  wordCount: number;
  /** Whole minutes at 200 words per minute, never below 1. */
  readingMinutes: number;
}

export interface PublicContentDocument extends PublicContentItem {
  bodyMarkdown: string;
  schema: Record<string, unknown> | null;
  variants: PublicContentVariantLink[];
  /** Optional call to action shown after the body. Both set, or both null. */
  ctaLabel: string | null;
  /** An https URL or a site path starting with "/". */
  ctaHref: string | null;
}

export interface PublicContentList {
  items: PublicContentItem[];
  page: number;
  limit: number;
  total: number;
  hasMore: boolean;
}

export interface PublicContentSitemapItem {
  documentId: string;
  kind: ContentKind;
  locale: ContentLocale;
  slug: string;
  updatedAt: string;
}

export interface PublicContentSitemap {
  items: PublicContentSitemapItem[];
  total: number;
}

export interface ContentEvidenceRecord {
  id: string;
  kind: string;
  sourceName: string;
  sourceUrl: string | null;
  title: string;
  excerpt: string | null;
  capturedAt: string;
  checksum: string;
}

export interface ContentClaimRecord {
  id: string;
  statement: string;
  status: ContentClaimStatus;
  evidenceIds: string[];
  riskLevel: ContentRiskLevel;
  notes: string | null;
}

export interface ContentRevisionRecord {
  id: string;
  variantId: string;
  revisionNumber: number;
  title: string;
  excerpt: string;
  bodyMarkdown: string;
  seoTitle: string | null;
  seoDescription: string | null;
  primaryKeyword: string | null;
  secondaryKeywords: string[];
  tags: string[];
  imageUrl: string | null;
  imageAlt: string | null;
  imageCaption: string | null;
  ogImageUrl: string | null;
  ogImageAlt: string | null;
  ctaLabel: string | null;
  ctaHref: string | null;
  authorName: string;
  reviewerName: string | null;
  schema: Record<string, unknown> | null;
  brief: Record<string, unknown> | null;
  qualityScore: number | null;
  createdByUserId: string;
  createdAt: string;
  evidence: ContentEvidenceRecord[];
  claims: ContentClaimRecord[];
}

export interface ContentVariantRecord {
  id: string;
  documentId: string;
  locale: ContentLocale;
  market: string;
  slug: string;
  status: ContentVariantStatus;
  automationMode: ContentAutomationMode;
  currentDraftRevisionId: string | null;
  publishedRevisionId: string | null;
  scheduledAt: string | null;
  publishedAt: string | null;
  noIndex: boolean;
  createdAt: string;
  updatedAt: string;
  currentDraftRevision?: ContentRevisionRecord | null;
  publishedRevision?: ContentRevisionRecord | null;
}

export interface ContentDocumentRecord {
  id: string;
  kind: ContentKind;
  topicKey: string | null;
  category: string | null;
  riskLevel: ContentRiskLevel;
  status: "active" | "archived";
  createdByUserId: string;
  updatedByUserId: string;
  createdAt: string;
  updatedAt: string;
  variants: ContentVariantRecord[];
}

export interface ContentAutomationPolicyRecord {
  id: string;
  market: string;
  locale: ContentLocale;
  kind: ContentKind;
  mode: ContentAutomationMode;
  allowedRiskLevels: ContentRiskLevel[];
  requireReview: boolean;
  maxPublishesPerDay: number;
  maxPublishesPerWeek: number;
  isActive: boolean;
  config: Record<string, unknown>;
  updatedByUserId: string;
  createdAt: string;
  updatedAt: string;
}

export interface ContentGenerationRunRecord {
  id: string;
  variantId: string;
  baseRevisionId: string | null;
  resultRevisionId: string | null;
  status: ContentGenerationStatus;
  mode: "draft" | "refresh" | "translate";
  idempotencyKey: string;
  provider: string | null;
  model: string | null;
  promptVersion: string;
  attempts: number;
  qualityResults: Record<string, unknown> | null;
  inputTokens: number | null;
  outputTokens: number | null;
  costUsdMicros: number | null;
  errorCode: string | null;
  errorMessage: string | null;
  startedAt: string | null;
  completedAt: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface ContentPublicationRecord {
  id: string;
  variantId: string;
  revisionId: string;
  status: ContentPublicationStatus;
  idempotencyKey: string;
  scheduledAt: string;
  attempts: number;
  publishedAt: string | null;
  errorCode: string | null;
  errorMessage: string | null;
  requestedByUserId: string;
  createdAt: string;
  updatedAt: string;
}
